import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { Box, IconButton, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useTagKey } from "../hooks/useTagKey";

const HeaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "4px",
  minWidth: 0,
}));

const EditableText = styled(Box)(({ theme }) => ({
  cursor: "pointer",
  borderRadius: "3px",
  padding: "0px 8px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TagContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
}));

const EditModeTextField = styled(TextField)(() => ({
  "& .MuiInputBase-input": {
    padding: "4px 8px",
  },
}));

const ActionsContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
}));

const ActionButton = styled(IconButton)(({ theme: _theme }) => ({
  "& .MuiSvgIcon-root": {
    fontSize: "16px",
  },
}));

// Don't show the buttons right now because we are short on space for the columns, and all functionality works with just clicking and typing
// But leaving the option here in case we want it later. The buttons make the edit functionality a bit more "discoverable".
const SHOW_ACTION_BUTTONS = false;

export const EditableTagHeader: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const { tagKey: currentTagKey, setTagKey } = useTagKey();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleEdit = useCallback(() => {
    setEditValue(currentTagKey);
    setIsEditing(true);
  }, [currentTagKey]);

  const handleSave = useCallback(() => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== currentTagKey) {
      setTagKey(trimmedValue);
    }
    setIsEditing(false);
  }, [editValue, currentTagKey, setTagKey]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValue("");
  }, []);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        handleSave();
      } else if (event.key === "Escape") {
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  // Handle click outside to confirm edit
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleSave();
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, handleSave]);

  return (
    <HeaderContainer ref={containerRef}>
      <TagContainer>
        <span>Tag: </span>
        {isEditing ? (
          <EditModeTextField
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyPress}
            autoFocus
            variant="outlined"
            size="small"
          />
        ) : (
          <EditableText onClick={handleEdit}>{currentTagKey}</EditableText>
        )}
      </TagContainer>
      {SHOW_ACTION_BUTTONS && (
        <ActionsContainer>
          {isEditing ? (
            <>
              <ActionButton onClick={handleSave} size="small" color="primary">
                <CheckIcon />
              </ActionButton>
              <ActionButton onClick={handleCancel} size="small">
                <CloseIcon />
              </ActionButton>
            </>
          ) : (
            <ActionButton onClick={handleEdit} size="small">
              <EditIcon />
            </ActionButton>
          )}
        </ActionsContainer>
      )}
    </HeaderContainer>
  );
};
